import { useState } from 'react';
import {
  type ColaboradorResumo,
  type SalvarColaboradorRequest,
} from '@/features/colaboradores/api/colaboradoresApi';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { apenasDigitos, formatarTelefone } from '@/shared/format/mascaras';

type Modo =
  | { tipo: 'novo' }
  | { tipo: 'editar'; colaborador: ColaboradorResumo };

type Props = {
  modo: Modo;
  aoFechar: () => void;
  aoSalvar: (dados: SalvarColaboradorRequest) => Promise<void>;
};

export function ColaboradorFormModal({ modo, aoFechar, aoSalvar }: Props) {
  const editando = modo.tipo === 'editar';
  const [nome, setNome] = useState(editando ? modo.colaborador.nome : '');
  const [cargo, setCargo] = useState(editando ? (modo.colaborador.cargo ?? '') : '');
  const [email, setEmail] = useState(editando ? (modo.colaborador.email ?? '') : '');
  const [telefone, setTelefone] = useState(
    editando ? formatarTelefone(modo.colaborador.telefone ?? '') : '',
  );
  const [ativo, setAtivo] = useState(editando ? modo.colaborador.ativo : true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      const telefoneDigitos = apenasDigitos(telefone);
      await aoSalvar({
        nome,
        cargo: cargo.trim() === '' ? null : cargo.trim(),
        email: email.trim() === '' ? null : email.trim(),
        telefone: telefoneDigitos === '' ? null : telefoneDigitos,
        ativo,
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto
      titulo={editando ? 'Editar colaborador' : 'Novo colaborador'}
      onFechar={aoFechar}
      rodape={
        <>
          <Button onClick={aoFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variante="primary"
            onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
            disabled={salvando}
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form className="form-grid" onSubmit={handleSubmit}>
        {erro && <div className="form-error">{erro}</div>}

        <Field rotulo="Nome" obrigatorio>
          {(id) => (
            <input
              id={id}
              className="input"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
              minLength={2}
              maxLength={200}
            />
          )}
        </Field>

        <Field rotulo="Cargo" dica="Opcional.">
          {(id) => (
            <input
              id={id}
              className="input"
              value={cargo}
              onChange={(e) => setCargo(e.target.value)}
              maxLength={100}
            />
          )}
        </Field>

        <Field rotulo="E-mail" dica="Opcional.">
          {(id) => (
            <input
              id={id}
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={200}
            />
          )}
        </Field>

        <Field rotulo="Telefone" dica="Opcional. Apenas números, com DDD.">
          {(id) => (
            <input
              id={id}
              className="input"
              type="tel"
              value={telefone}
              onChange={(e) => setTelefone(formatarTelefone(e.target.value))}
              maxLength={16}
              inputMode="numeric"
            />
          )}
        </Field>

        <label className="checkbox">
          <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
          <span>Colaborador ativo</span>
        </label>
      </form>
    </Modal>
  );
}
